import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { pluralized } from '~/components/filters';
import { threadMessageContent } from '../messageHubUtil';
import { useMessageHub } from '../useMessageHub';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { useToasted } from '~/composables/useToasted';
import { FEATURES } from '~/provides/features';
import { contactsClient, messagesClient, scheduledMessagesClient, threadsClient } from '~/services';
import Bus from '~/globals/eventBus';
import { catchRequestError } from '~/utils/request';
import { hasScope } from '~/utils/users';
import { HTTP_CONFLICT } from '~/support/HttpStatuses';
import {
    faCircleMinus,
    faCirclePlus,
    faEnvelope,
    faEnvelopeOpen,
    faMessageMinus,
    faUserPen,
    faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createGlobalState } from '@vueuse/core';
import type { Thread } from '~/services/threads/threadsClient.types';
import type { Message } from '~/types/message.types';
import type { AxiosResponse } from 'axios';

interface BulkActionOptions {
    showUndo?: boolean;
}

interface MessageData {
    body: string;
    media_url: string | null;
    media_content_type: string | null;
    send_at: string | null;
}

interface SnippetData {
    body: string;
    media_url: string | null;
    media_content_type: string | null;
}

interface Action {
    id: string;
    icon: IconDefinition;
    label: string;
    color?: string;
    show: boolean;
    loading: boolean;
    handler: () => void;
}

interface PreviousAssignment {
    thread: Thread;
    previousAssigneeId: number | null;
    previousStatus: string;
}

export const useMessageHubBulkActions = createGlobalState(() => {
    const { selectedThreads, removeThreadSelection, agents } = useMessageHub();
    const { isMd } = useResponsiveLayout();
    const toasted = useToasted();

    const affectedThreads: Ref<Thread[]> = ref([]);
    const success = ref('');
    const error = ref('');

    const closingThreads = ref(false);
    const openingThreads = ref(false);
    const markingAsRead = ref(false);
    const markingAsUnread = ref(false);
    const claimingThreads = ref(false);
    const assigningThreads = ref(false);
    const sendingMessage = ref(false);
    const unsubscribingContacts = ref(false);

    const closeWithSnippetModal = ref(false);
    const assignModal = ref(false);

    const undoAction: Ref<(() => void | Promise<void>) | null> = ref(null);
    const undoing = ref(false);

    const message: Ref<MessageData> = ref({
        body: '',
        media_url: null,
        media_content_type: null,
        send_at: null,
    });

    const executeUndo = async (): Promise<void> => {
        if (!undoAction.value || undoing.value) {
            return;
        }

        undoing.value = true;
        try {
            await undoAction.value();
            undoAction.value = null;
        } catch (e) {
            showNotification(catchRequestError(e), 'error');
        } finally {
            undoing.value = false;
        }
    };

    const resetMessageData = (): void => {
        message.value.body = '';
        message.value.media_url = null;
        message.value.media_content_type = null;
        message.value.send_at = null;
    };

    const reset = (): void => {
        success.value = '';
        error.value = '';
        closeWithSnippetModal.value = false;
        assignModal.value = false;
    };

    const showNotification = (messageText: string, type: string, showUndo = false): void => {
        if (isMd.value) {
            if (type === 'success') {
                success.value = messageText;
            } else {
                error.value = messageText;
            }
        } else {
            const toastConfig: {
                title: string;
                type: string;
                action?: {
                    label: string;
                    onClick: () => Promise<void>;
                };
            } = {
                title: messageText,
                type: type,
            };

            if (showUndo) {
                toastConfig.action = {
                    label: 'Undo',
                    onClick: async () => {
                        toasted.dismiss(toastId);
                        await executeUndo();
                    },
                };
            }

            const toastId = toasted.show(toastConfig);
        }
    };

    const bulkCloseThreads = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (closingThreads.value || threads.length === 0) {
            return;
        }

        closingThreads.value = true;

        try {
            const promises = threads.map((thread) => {
                if (thread.status === 'closed') {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                const context = { teamId: thread.team_id, groupId: thread.group_id };
                return threadsClient.setState(context, thread.contact_id, 'closed', thread.group_id).then(() => {
                    thread.status = 'closed';
                    Bus.$emit('threadUpdated', { thread: { ...thread } });
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} closed successfully.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkOpenThreads(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to close ${pluralized('thread', errorCount, true)}.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            closingThreads.value = false;
        }
    };

    const bulkOpenThreads = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (openingThreads.value || threads.length === 0) {
            return;
        }

        openingThreads.value = true;

        try {
            const promises = threads.map((thread) => {
                if (thread.status === 'open') {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                const context = { teamId: thread.team_id, groupId: thread.group_id };
                return threadsClient.setState(context, thread.contact_id, 'open', thread.group_id).then(() => {
                    thread.status = 'open';
                    Bus.$emit('threadUpdated', { thread: { ...thread } });
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} opened successfully.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkCloseThreads(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to open ${pluralized('thread', errorCount, true)}.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            openingThreads.value = false;
        }
    };

    const bulkMarkAsRead = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (markingAsRead.value || threads.length === 0) {
            return;
        }

        markingAsRead.value = true;

        try {
            const promises = threads.map((thread) => {
                if (!thread.unread) {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                const context = { teamId: thread.team_id, groupId: thread.group_id };
                return threadsClient.checkpointSave(context, thread.contact_id, thread.group_id).then(() => {
                    thread.unread = 0;
                    Bus.$emit('threadUpdated', { thread: { ...thread } });
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} marked as read.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkMarkAsUnread(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to mark ${pluralized('thread', errorCount, true)} as read.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            markingAsRead.value = false;
        }
    };

    const bulkMarkAsUnread = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (markingAsUnread.value || threads.length === 0) {
            return;
        }

        markingAsUnread.value = true;

        try {
            // only 1-1 messages make a conversation unread; null, unlike a missing value, means the API found none
            threads
                .filter((thread) => null === thread.last_interaction_at)
                .forEach((thread) => removeThreadSelection(thread));

            const promises = threads
                .filter((thread) => null !== thread.last_interaction_at)
                .map((thread) => {
                    if (thread.unread) {
                        removeThreadSelection(thread);
                        return Promise.resolve(thread);
                    }

                    const context = { teamId: thread.team_id, groupId: thread.group_id };
                    return threadsClient.checkpointDelete(context, thread.contact_id, thread.group_id).then(() => {
                        thread.unread = 1;
                        Bus.$emit('threadUpdated', { thread: { ...thread } });
                        removeThreadSelection(thread);
                        return thread;
                    });
                });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} marked as unread.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkMarkAsRead(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to mark ${pluralized('thread', errorCount, true)} as unread.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            markingAsUnread.value = false;
        }
    };

    const bulkClaimThreads = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (claimingThreads.value || threads.length === 0) {
            return;
        }

        const previousAssignments: PreviousAssignment[] = threads.map((thread) => ({
            thread: { ...thread },
            previousAssigneeId: thread.assignee_id,
            previousStatus: thread.status,
        }));

        claimingThreads.value = true;

        try {
            const claimPromises = threads.map(async (thread) => {
                if (thread.assignee_id === Spark.state.user.id) {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                const context = { teamId: thread.team_id, groupId: thread.group_id };

                if (thread.status !== 'open') {
                    await threadsClient.setState(context, thread.contact_id, 'open', thread.group_id);
                }

                return threadsClient.assignToSelf(context, thread.contact_id, thread.group_id).then(() => {
                    thread.status = 'open';
                    thread.assignee_id = Spark.state.user.id;
                    Bus.$emit('threadUpdated', { thread: { ...thread } });
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(claimPromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                const successMessage = `${pluralized('thread', successCount, true)} claimed successfully.`;
                undoAction.value = async () => {
                    for (const { thread, previousAssigneeId, previousStatus } of previousAssignments) {
                        if (previousAssigneeId === null) {
                            await unassignThread(thread, { showUndo: false });
                        }
                        if (previousStatus === 'closed') {
                            await bulkCloseThreads([thread], { showUndo: false });
                        }
                    }
                };
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to claim ${pluralized('thread', errorCount, true)}.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            claimingThreads.value = false;
        }
    };

    const unassignThread = async (thread: Thread, options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        const context = { teamId: thread.team_id, groupId: thread.group_id };
        await threadsClient.unclaim(context, thread.contact_id, thread.group_id);
        thread.assignee_id = null;
        thread.status = 'open';
        Bus.$emit('threadUpdated', { thread: { ...thread } });
        removeThreadSelection(thread);
    };

    const getAssigneeName = (assigneeId: number | string): string => {
        if (assigneeId === 'unassigned') {
            return 'Unassigned';
        }
        if (assigneeId === Spark.state.user.id) {
            return 'You';
        }
        const agent = agents.value.find((agent) => agent.id === assigneeId);
        return agent ? agent.name : 'Unknown Agent';
    };

    const handleBulkAssign = async (
        assigneeId: number | string,
        threads: Thread[],
        options: BulkActionOptions = {}
    ): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (assigningThreads.value || threads.length === 0) {
            return;
        }

        const previousAssignments: PreviousAssignment[] = threads.map((thread) => ({
            thread: { ...thread },
            previousAssigneeId: thread.assignee_id,
            previousStatus: thread.status,
        }));

        assigningThreads.value = true;

        try {
            const assignPromises = threads.map(async (thread) => {
                const context = { teamId: thread.team_id, groupId: thread.group_id };

                if (thread.status !== 'open') {
                    await threadsClient.setState(context, thread.contact_id, 'open', thread.group_id);
                }

                if (assigneeId === 'unassigned') {
                    return threadsClient.unclaim(context, thread.contact_id, thread.group_id).then(() => {
                        thread.status = 'open';
                        thread.assignee_id = null;
                        Bus.$emit('threadUpdated', { thread: { ...thread } });
                        removeThreadSelection(thread);
                        return thread;
                    });
                } else {
                    const assignToSelf = assigneeId === Spark.state.user.id;
                    if (assignToSelf) {
                        return threadsClient.assignToSelf(context, thread.contact_id, thread.group_id).then(() => {
                            thread.status = 'open';
                            thread.assignee_id = Spark.state.user.id;
                            Bus.$emit('threadUpdated', { thread: { ...thread } });
                            removeThreadSelection(thread);
                            return thread;
                        });
                    } else {
                        return threadsClient
                            .assignTo(
                                context,
                                thread.contact_id,
                                { assignee_id: assigneeId as number },
                                thread.group_id
                            )
                            .then(() => {
                                thread.status = 'open';
                                thread.assignee_id = assigneeId as number;
                                Bus.$emit('threadUpdated', { thread: { ...thread } });
                                removeThreadSelection(thread);
                                return thread;
                            });
                    }
                }
            });

            const results = await Promise.allSettled(assignPromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                const message =
                    assigneeId === 'unassigned'
                        ? `${pluralized('thread', successCount, true)} unassigned successfully.`
                        : `${pluralized('thread', successCount, true)} assigned to ${getAssigneeName(
                              assigneeId
                          )} successfully.`;

                undoAction.value = async () => {
                    for (const { thread, previousAssigneeId, previousStatus } of previousAssignments) {
                        if (previousAssigneeId === null) {
                            await unassignThread(thread, { showUndo: false });
                        } else {
                            await handleBulkAssign(previousAssigneeId, [thread], { showUndo: false });
                        }
                        if (previousStatus === 'closed') {
                            await bulkCloseThreads([thread], { showUndo: false });
                        }
                    }
                };
                showNotification(message, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                const message =
                    assigneeId === 'unassigned'
                        ? `Failed to unassign ${pluralized('thread', errorCount, true)}.`
                        : `Failed to assign ${pluralized('thread', errorCount, true)} to ${getAssigneeName(
                              assigneeId
                          )}.`;
                showNotification(message, 'error');
            } else {
                error.value = '';
            }

            assignModal.value = false;
        } finally {
            assigningThreads.value = false;
        }
    };

    const closeWithSnippet = async (
        { body, media_url, media_content_type }: SnippetData,
        options: BulkActionOptions = {},
        targetThreads?: Thread[]
    ): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        const threads = targetThreads || Array.from(selectedThreads.value.values());

        if (threads.length === 0) {
            return;
        }

        try {
            const closePromises = threads.map(async (thread) => {
                const payload = {
                    contact_id: thread.contact_id,
                    body,
                    media_url,
                    media_content_type,
                };

                if (thread.group_id) {
                    await messagesClient.groupMessageCreate(thread.team_id, thread.group_id, payload);
                } else {
                    await messagesClient.createV3(thread.team_id, payload);
                }

                const context = { teamId: thread.team_id, groupId: thread.group_id };
                return threadsClient.setState(context, thread.contact_id, 'closed', thread.group_id).then(() => {
                    thread.status = 'closed';
                    Bus.$emit('threadUpdated', { thread: { ...thread } });
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(closePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                const successMessage = `${pluralized('thread', successCount, true)} closed with snippet.`;
                const successfulResults = results.filter((result) => result.status === 'fulfilled');
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkOpenThreads(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to close ${pluralized('thread', errorCount, true)} with snippet.`, 'error');
            } else {
                error.value = '';
            }

            closeWithSnippetModal.value = false;
        } catch (e) {
            error.value = catchRequestError(e);
        }
    };

    const bulkUnsubscribeContacts = async (threads: Thread[]): Promise<void> => {
        if (unsubscribingContacts.value || threads.length === 0) {
            return;
        }

        unsubscribingContacts.value = true;

        try {
            const unsubscribePromises = threads.map(async (thread) => {
                const marketingPromise = contactsClient
                    .contactSubscriptionOptOut(thread.team_id, thread.contact_id, 'marketing')
                    .catch((error) => {
                        if (error?.response?.status !== HTTP_CONFLICT) {
                            throw error;
                        }
                        return thread;
                    });

                const transactionalPromise = contactsClient
                    .contactSubscriptionOptOut(thread.team_id, thread.contact_id, 'transactional')
                    .catch((error) => {
                        if (error?.response?.status !== HTTP_CONFLICT) {
                            throw error;
                        }
                        return thread;
                    });

                return Promise.all([marketingPromise, transactionalPromise]).then(() => {
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(unsubscribePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                success.value = `${pluralized('contact', successCount, true)} unsubscribed successfully.`;
            } else {
                success.value = '';
            }
            if (errorCount > 0) {
                error.value = `Failed to unsubscribe ${pluralized('contact', errorCount, true)}.`;
            } else {
                error.value = '';
            }
        } finally {
            unsubscribingContacts.value = false;
        }
    };

    const sendBulkMessage = async (
        targetThreads: Thread[],
        messageData: { body: string; media_url: string | null; media_content_type: string | null }
    ): Promise<void> => {
        if (sendingMessage.value || targetThreads.length === 0 || !messageData.body.trim()) {
            return;
        }

        sendingMessage.value = true;

        try {
            const messagePromises = targetThreads.map((thread) => {
                const payload = {
                    contact_id: thread.contact_id,
                    body: messageData.body,
                    media_url: messageData.media_url,
                    media_content_type: messageData.media_content_type,
                };

                const emitThreadUpdated = (sentMessage: Message): void => {
                    const content = threadMessageContent(sentMessage);

                    Bus.$emit('threadUpdated', {
                        thread: {
                            ...thread,
                            last_message_id: sentMessage.id,
                            last_message_content: content,
                            last_message_direction: 'outbound',
                            last_message_at: sentMessage.created_at,
                            last_interaction_message_id: sentMessage.id,
                            last_interaction_content: content,
                            last_interaction_direction: 'outbound',
                            last_interaction_at: sentMessage.created_at,
                        },
                    });
                };

                const request: Promise<AxiosResponse<Message>> = thread.group_id
                    ? messagesClient.groupMessageCreate(thread.team_id, thread.group_id, payload)
                    : messagesClient.createV3(thread.team_id, payload);

                return request.then((result) => {
                    if (result.data?.id) {
                        emitThreadUpdated(result.data);
                    }

                    removeThreadSelection(thread);

                    return result;
                });
            });

            const results = await Promise.allSettled(messagePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                success.value = `Message sent to ${pluralized('contact', successCount, true)}.`;
            } else {
                success.value = '';
            }
            if (errorCount > 0) {
                error.value = `Failed to send message to ${pluralized('contact', errorCount, true)}.`;
            } else {
                error.value = '';
            }
        } finally {
            sendingMessage.value = false;
        }
    };

    const scheduleBulkMessage = async (
        scheduleAt: string,
        targetThreads: Thread[],
        messageData: { body: string; media_url: string | null; media_content_type: string | null }
    ): Promise<void> => {
        if (sendingMessage.value || targetThreads.length === 0 || !messageData.body.trim()) {
            return;
        }

        sendingMessage.value = true;

        try {
            const schedulePromises = targetThreads.map((thread) => {
                const payload = {
                    contact_id: thread.contact_id,
                    body: messageData.body,
                    media_url: messageData.media_url,
                    media_content_type: messageData.media_content_type,
                    send_at: scheduleAt,
                };

                return scheduledMessagesClient
                    .createScheduledMessage(
                        { teamId: thread.team_id, groupId: thread.group_id },
                        thread.contact_id,
                        payload
                    )
                    .then((result) => {
                        removeThreadSelection(thread);
                        return result;
                    });
            });

            const results = await Promise.allSettled(schedulePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                success.value = `Message scheduled for ${pluralized('contact', successCount, true)}.`;
            } else {
                success.value = '';
            }
            if (errorCount > 0) {
                error.value = `Failed to schedule message for ${pluralized('contact', errorCount, true)}.`;
            } else {
                error.value = '';
            }
        } finally {
            sendingMessage.value = false;
        }
    };

    const openAssignModal = (): void => {
        assignModal.value = true;
    };

    const getActionsForThreads = (threadsArray: Thread[]): Action[] => {
        const hasOpen = threadsArray.length === 0 || threadsArray.some((t) => t.status === 'open');
        const hasClosed = threadsArray.length === 0 || threadsArray.some((t) => t.status !== 'open');
        const hasUnread = threadsArray.length === 0 || threadsArray.some((t) => Boolean(t.unread));
        const hasRead = threadsArray.length === 0 || threadsArray.some((t) => !t.unread);
        const hasUnclaimed =
            threadsArray.length === 0 || threadsArray.some((t: Thread) => t.assignee_id !== Spark.state.user.id);

        if (FEATURES.simple_message_hub) {
            return [
                {
                    id: 'unread',
                    icon: faEnvelope,
                    label: 'Unread',
                    show: hasClosed,
                    loading: openingThreads.value,
                    handler: () => bulkOpenThreads(threadsArray),
                },
                {
                    id: 'read',
                    icon: faEnvelopeOpen,
                    label: 'Read',
                    show: hasOpen,
                    loading: closingThreads.value,
                    handler: () => bulkCloseThreads(threadsArray),
                },
            ];
        }

        return [
            {
                id: 'close',
                icon: faCircleMinus,
                label: 'Close',
                color: 'text-rose-500',
                show: hasOpen,
                loading: closingThreads.value,
                handler: () => bulkCloseThreads(threadsArray),
            },
            {
                id: 'close-with-snippet',
                icon: faMessageMinus,
                label: 'Close w/ Snippet',
                show: hasOpen && isMd.value,
                loading: false,
                handler: () => (closeWithSnippetModal.value = true),
            },
            {
                id: 'open',
                icon: faCirclePlus,
                label: 'Open',
                show: hasClosed,
                loading: openingThreads.value,
                handler: () => bulkOpenThreads(threadsArray),
            },
            {
                id: 'unread',
                icon: faEnvelope,
                label: 'Mark Unread',
                show: hasRead,
                loading: markingAsUnread.value,
                handler: () => bulkMarkAsUnread(threadsArray),
            },
            {
                id: 'read',
                icon: faEnvelopeOpen,
                label: 'Mark Read',
                show: hasUnread,
                loading: markingAsRead.value,
                handler: () => bulkMarkAsRead(threadsArray),
            },
            {
                id: 'claim',
                icon: faUserPlus,
                label: 'Claim',
                show: hasScope('threads:claim') && !hasScope('threads:assign') && hasUnclaimed,
                loading: claimingThreads.value,
                handler: () => bulkClaimThreads(threadsArray),
            },
            {
                id: 'assign',
                icon: faUserPen,
                label: 'Assign',
                show: hasScope('threads:assign'),
                loading: assigningThreads.value,
                handler: openAssignModal,
            },
        ];
    };

    const actions: ComputedRef<Action[]> = computed(() => {
        return getActionsForThreads(Array.from(selectedThreads.value.values()));
    });

    return {
        bulkCloseThreads,
        bulkOpenThreads,
        bulkMarkAsRead,
        bulkMarkAsUnread,
        bulkClaimThreads,
        handleBulkAssign,
        closeWithSnippet,
        bulkUnsubscribeContacts,
        sendBulkMessage,
        scheduleBulkMessage,

        actions,
        getActionsForThreads,
        closeWithSnippetModal,
        assignModal,

        message,
        resetMessageData,
        reset,
        sendingMessage,
        closingThreads,
        openingThreads,
        markingAsRead,
        markingAsUnread,
        claimingThreads,
        unsubscribingContacts,

        success,
        error,
        affectedThreads,

        executeUndo,
        undoAction,
        undoing,
    };
});
